<script setup lang="ts">
import type { PropertyOnList } from '@@/shared/property/api-types';
import type { PropertyOnCard } from '@@/shared/property/types';

const props = defineProps<{
  property: PropertyOnList | PropertyOnCard
}>();

const { title: propertyTitle } = usePropertyTitle(toRef(() => props.property));

const runtimeConfig = useRuntimeConfig();
// max width on responsive design (single image on small devices)
// https://sharp.pixelplumbing.com/api-input
const IMG_WIDTH = 300;
const photo = computed(() => {
  const src = props.property.photos[0];
  if (!src) return;
  return getImageResize({
    src,
    width: IMG_WIDTH,
    resizeHostname: runtimeConfig.public.img.resizeHostname,
  });
});
</script>

<template>
  <article class="overflow-clip relative isolate flex flex-col">
    <span
      class="font-title text-2xl bg-secondary text-white font-semibold uppercase absolute -left-20 w-80 py-s top-36 text-center -rotate-45 origin-top-left z-20"
      v-text="property.transactionType === 'sale' ? $t('property.tags.sold') : $t('property.tags.rented')"
    />
    <picture class="aspect-square rounded-xl overflow-clip">
      <img
        v-if="photo"
        :alt="propertyTitle"
        v-bind="photo"
        :width="IMG_WIDTH"
        :height="IMG_WIDTH"
        loading="lazy"
        class="w-full object-cover aspect-square"
      >
      <div
        v-else
        class="bg-neutral-80 w-full flex flex-col gap-m items-center justify-center text-secondary aspect-square"
      >
        <Icon
          name="iad-icon:logo-iad"
          class="size-24"
        />
        <IadTitle
          :text="$t(`property.common.no_photo`)"
          tag="p"
          variant="x-small"
        />
      </div>
    </picture>
    <div
      class="flex-1 flex flex-col pt-xs pb-m"
      :class="{
        'flex flex-col gap-s': property.transactionBuyerAgent,
      }"
    >
      <IadTitle
        tag="p"
        variant="x-small"
        :text="propertyTitle"
      />

      <IadLink
        v-if="property.transactionBuyerAgent"
        class="flex justify-between py-xs"
        variant="colored"
        :to="property.transactionBuyerAgent.to"
      >
        <span v-text="property.transactionBuyerAgent.text" />
        <Icon
          name="iad-icon:arrow-forward"
          class="text-primary-40 mt-2xs"
        />
      </IadLink>

      <PropertyCardAgent
        v-if="'agent' in property"
        :agent="property.agent"
        :property
      />
    </div>
  </article>
</template>
